import { Action } from '@ngrx/store';
import { Reference } from '../../utils/action-helper.util';
import { Error } from '../_models/auth.models';
import {
  CreateRelationPayload,
  CreateRelationResponse,
  Relation,
  RelationTable,
  UpdateRelationPayload,
  Validate,
} from '../_models/relation.models';

/**
 * Relation action types
 */
export enum RelationActionTypes {
  LOAD_RELATION = '[Relation] Load Relation',
  LOAD_RELATION_SUCCESS = '[Relation] Load Relation Success',
  LOAD_RELATION_FAILED = '[Relation] Load Relation Failed',
  LOAD_RELATIONS = '[Relation] Load Relations',
  LOAD_RELATIONS_SUCCESS = '[Relation] Load Relations Success',
  LOAD_RELATIONS_FAILED = '[Relation] Load Relations Failed',
  LOAD_APPLICATION_RELATIONS = '[Relation] Load Application Relations',
  LOAD_APPLICATION_RELATIONS_SUCCESS = '[Relation] Load Application Relations Success',
  LOAD_APPLICATION_RELATIONS_FAILED = '[Relation] Load Application Relations Failed',
  VALIDATE_RELATION = '[Relation] Validate Relation',
  VALIDATE_RELATION_SUCCESS = '[Relation] Validate Relation Success',
  VALIDATE_RELATION_FAILED = '[Relation] Validate Relation Failed',
  CREATE_RELATION = '[Relation] Create Relation',
  CREATE_RELATION_SUCCESS = '[Relation] Create Relation Success',
  CREATE_RELATION_FAILED = '[Relation] Create Relation Failed',
  DELETE_RELATION = '[Relation] Delete Relation',
  DELETE_RELATION_SUCCESS = '[Relation] Delete Relation Success',
  DELETE_RELATION_FAILED = '[Relation] Delete Relation Failed',
  UPDATE_RELATION = '[Relation] Update Relation',
  UPDATE_RELATION_SUCCESS = '[Relation] Update Relation Success',
  UPDATE_RELATION_FAILED = '[Relation] Update Relation Failed',
  LOAD_RELATION_TABLES = '[Relation] Load Relation Tables',
  LOAD_RELATION_TABLES_SUCCESS = '[Relation] Load Relation Tables Success',
  LOAD_RELATION_TABLES_FAILED = '[Relation] Load Relation Tables Failed',
  RESET = '[Relation] RESET',
}

/**
 * Operations
 */
export class LoadRelation implements Action {
  readonly type = RelationActionTypes.LOAD_RELATION;

  constructor(
    public tableId: string,
    public relationId: string,
    public force: boolean = false
  ) {}
}

export class LoadRelationSuccess implements Action {
  readonly type = RelationActionTypes.LOAD_RELATION_SUCCESS;

  constructor(
    public tableId: string,
    public relationId: string,
    public payload: Relation
  ) {}
}

export class LoadRelationFailed implements Action {
  readonly type = RelationActionTypes.LOAD_RELATION_FAILED;

  constructor(public payload: Error, public tableId: string) {}
}

export class LoadRelations implements Action {
  readonly type = RelationActionTypes.LOAD_RELATIONS;

  /**
   * @param force to forcefully fetch, regardless of caching enabled
   */
  constructor(public tableId: string, public force: boolean = false) {}
}

export class LoadRelationsSuccess implements Action {
  readonly type = RelationActionTypes.LOAD_RELATIONS_SUCCESS;

  constructor(public tableId: string, public payload: Array<Relation>) {}
}

export class LoadRelationsFailed implements Action {
  readonly type = RelationActionTypes.LOAD_RELATIONS_FAILED;

  constructor(public payload: Error, public tableId: string) {}
}

export class LoadApplicationRelations implements Action {
  readonly type = RelationActionTypes.LOAD_APPLICATION_RELATIONS;

  /**
   * @param force to forcefully fetch, regardless of caching enabled
   */
  constructor(public appId: string, public force: boolean = false) {}
}

export class LoadApplicationRelationsSuccess implements Action {
  readonly type = RelationActionTypes.LOAD_APPLICATION_RELATIONS_SUCCESS;

  constructor(public appId: string, public payload: Array<Relation>) {}
}

export class LoadApplicationRelationsFailed implements Action {
  readonly type = RelationActionTypes.LOAD_APPLICATION_RELATIONS_FAILED;

  constructor(public payload: Error) {}
}

export class ValidateRelation implements Action {
  readonly type = RelationActionTypes.VALIDATE_RELATION;

  /**
   * @param force to forcefully fetch, regardless of caching enabled
   */
  constructor(
    public fromTableId: any,
    public toTableId: any,
    public force: boolean = false
  ) {}
}

export class ValidateRelationSuccess implements Action {
  readonly type = RelationActionTypes.VALIDATE_RELATION_SUCCESS;

  constructor(
    public fromTableId: any,
    public toTableId: any,
    public payload: Validate
  ) {}
}

export class ValidateRelationFailed implements Action {
  readonly type = RelationActionTypes.VALIDATE_RELATION_FAILED;

  constructor(public payload: Error) {}
}

export class CreateRelation extends Reference implements Action {
  readonly type = RelationActionTypes.CREATE_RELATION;

  constructor(public payload: CreateRelationPayload) {
    super();
  }
}

export class CreateRelationSuccess extends Reference implements Action {
  readonly type = RelationActionTypes.CREATE_RELATION_SUCCESS;

  constructor(public payload: CreateRelationResponse) {
    super();
  }
}

export class CreateRelationFailed extends Reference implements Action {
  readonly type = RelationActionTypes.CREATE_RELATION_FAILED;

  constructor(public payload: Error) {
    super();
  }
}

export class DeleteRelation extends Reference implements Action {
  readonly type = RelationActionTypes.DELETE_RELATION;

  constructor(public tableId: string, public relationId: string) {
    super();
  }
}

export class DeleteRelationSuccess extends Reference implements Action {
  readonly type = RelationActionTypes.DELETE_RELATION_SUCCESS;

  constructor(public tableId: string, public relationId: string) {
    super();
  }
}

export class DeleteRelationFailed extends Reference implements Action {
  readonly type = RelationActionTypes.DELETE_RELATION_FAILED;

  constructor(public payload: Error) {
    super();
  }
}

export class UpdateRelation extends Reference implements Action {
  readonly type = RelationActionTypes.UPDATE_RELATION;

  constructor(public payload: UpdateRelationPayload) {
    super();
  }
}

export class UpdateRelationSuccess extends Reference implements Action {
  readonly type = RelationActionTypes.UPDATE_RELATION_SUCCESS;

  constructor() {
    super();
  }
}

export class UpdateRelationFailed extends Reference implements Action {
  readonly type = RelationActionTypes.UPDATE_RELATION_FAILED;

  constructor(public payload: Error) {
    super();
  }
}

export class LoadRelationTables implements Action {
  readonly type = RelationActionTypes.LOAD_RELATION_TABLES;

  constructor(
    public applicationId: string,
    public tableId: string,
    public force: boolean = false
  ) {}
}

export class LoadRelationTablesSuccess implements Action {
  readonly type = RelationActionTypes.LOAD_RELATION_TABLES_SUCCESS;

  constructor(
    public applicationId: string,
    public tableId: string,
    public payload: Array<RelationTable>
  ) {}
}

export class LoadRelationTablesFailed implements Action {
  readonly type = RelationActionTypes.LOAD_RELATION_TABLES_FAILED;

  constructor(public payload: Error, public tableId: string) {}
}

export class Reset implements Action {
  readonly type = RelationActionTypes.RESET;

  constructor() {}
}

/**
 * Available types
 */
export type Actions =
  | LoadRelation
  | LoadRelationSuccess
  | LoadRelationFailed
  | LoadRelations
  | LoadRelationsSuccess
  | LoadRelationsFailed
  | ValidateRelation
  | ValidateRelationSuccess
  | ValidateRelationFailed
  | CreateRelation
  | CreateRelationSuccess
  | CreateRelationFailed
  | DeleteRelation
  | DeleteRelationSuccess
  | DeleteRelationFailed
  | UpdateRelation
  | UpdateRelationSuccess
  | UpdateRelationFailed
  | LoadRelationTables
  | LoadRelationTablesSuccess
  | LoadRelationTablesFailed
  | LoadApplicationRelations
  | LoadApplicationRelationsSuccess
  | LoadApplicationRelationsFailed
  | Reset;
